import BlendMode from './BlendMode';
import { FrameType } from './FrameType';

import { Soundtrack } from '../audio/Soundtrack';
import FlyerType from './FlyerType';
import { MotionSourceType } from '../motion/MotionSource';
import { Resolution } from './Resolution';
import {
  BackgroundRepeat,
  Effect,
  EffectData,
  SoundWaveType
} from '@beatflyer/shaders-lib';
import AudioSource from '../audio/AudioSource';
import { Frame } from './Frame';
import { getNodeById, getRenderer } from '../state/Store';
import Layer from './Layer';
import { getPolygonPoints, getShapeRectangle } from 'utils/GraphicsUtils';
import { CaptionsToken } from './CaptionsToken';
import { TextTransform } from './TextTransform';
import { CaptionsDisplayMode } from 'captions/CaptionsDisplayMode';
import { cloneObject } from 'utils';

const PIXI = (window as any).PIXI;
export interface MotionSourceData {
  bpm: number;
  type: MotionSourceType;
  maxDuration: number;
}

export interface MotionSourceBinaryData extends MotionSourceData {
  cx: number;
}

export interface CurveData extends MotionSourceBinaryData {
  l: number;
  cl: number;
  cr: number;
  r: number;
}
export interface PointData {
  x: number;
  y: number;
}

export interface RectData {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface SimpleShapeData extends RectData {
  isOval?: boolean;
  cornerRadius?: number;
}

export interface PolygonShapeData extends RectData {
  corners: number;
}

export const isSimpleShape = (obj: ShapeData): obj is SimpleShapeData => {
  return 'width' in obj && 'height' in obj && 'x' in obj && 'y' in obj;
};

export const isRectangleShape = (obj: ShapeData): boolean => {
  return (
    isSimpleShape(obj) &&
    (obj as SimpleShapeData).isOval !== true &&
    !('corners' in obj)
  );
};

export const isOvalShape = (obj: ShapeData): boolean => {
  return isSimpleShape(obj) && (obj as SimpleShapeData).isOval === true;
};

export const getShapePoints = (shape: ShapeData): PointData[] => {
  if (Array.isArray(shape)) {
    return cloneObject(shape);
  }
  if ('corners' in shape) {
    return getPolygonPoints(shape);
  }
  const points: PointData[] = [];
  if (isSimpleShape(shape)) {
    points.push({ x: shape.x, y: shape.y });
    points.push({ x: shape.x + shape.width, y: shape.y });
    points.push({
      x: shape.x + shape.width,
      y: shape.y + shape.height
    });
    points.push({ x: shape.x, y: shape.y + shape.height });
  }
  return points;
};

export type ShapeData = SimpleShapeData | PointData[] | PolygonShapeData;

export interface FrameData {
  type: FrameType;
  id?: string;
  relativeDuration?: number;
  rotation?: number;
}

export interface PictureFrameData extends FrameData {
  url: string;
  name?: string;
  box: RectData;
}

export interface TextFrameData extends FrameData {
  text: string;
  textTransform?: TextTransform;
  color: string | string[];
  family: string;
  weight: number;
  size: number;
  style?: string;
  box: RectData;
  alignment: string[];
  lineHeight: number;
  letterSpacing: number;

  fillGradientType?: number;

  dropShadowAngle?: number;
  dropShadowBlur?: number;
  dropShadowColor?: string;
  dropShadowDistance?: number;

  stroke?: string;
  strokeThickness?: number;
}

export interface CaptionsFrameData extends TextFrameData {
  displayMode: CaptionsDisplayMode;
  backgroundColor?: string;
  animated?: boolean;
}

export interface SolidFrameData extends FrameData {
  color: string;
}

export interface GradientFrameData extends FrameData {
  colors: string[];
}

export interface ShapeFrameData extends GradientFrameData {
  mask: ShapeData;
  hole?: ShapeData;
}

export interface SoundWaveFrameData extends FrameData {
  waveType: SoundWaveType;
  box: RectData;
  effectProperties: any;
}

export interface CompositionData {
  id: string;
  props: any;
  visible?: boolean;
}

export interface LayerData {
  id?: string;
  frames: FrameData[];
  effects?: EffectData[];
  compositionEffect?: CompositionData;
  syncPlayback?: boolean;
  visible?: boolean;
  locked?: boolean;
  repeat?: BackgroundRepeat;
  blendMode?: BlendMode;
  audioSource?: AudioSource;
  stageWidth?: number;
  stageHeight?: number;
  pixelRatio?: number;

  timeline?: any;
  mask?: ShapeData;
  maskRotation?: number;
}

export enum TemplateUIType {
  Boolean = 'bool',
  Text = 'text',
  TextArea = 'textarea',
  Color = 'color',
  Image = 'image',
  Int = 'int',
  Float = 'float',
  Select = 'select'
}

export enum TemplateUIIntroType {
  None = 'none',
  Fade = 'fade',
  Slide = 'slide'
}

export const TEMPLATE_UI_INTROS = [
  TemplateUIIntroType.None,
  TemplateUIIntroType.Fade,
  TemplateUIIntroType.Slide
];

export enum TemplateImageContentMode {
  Fill = 'fill',
  Fit = 'fit'
}

export interface TemplateUIBindOptions {
  rect?: RectData;
  contentMode?: TemplateImageContentMode;
  forceOneLine?: boolean;
  removeBackground?: boolean;
}

export interface BindComponents {
  node: Layer | Frame | null;
  effect: Effect | null;
  propertyName: string;
}

export const parseBindPath = (bind: string): BindComponents => {
  const components = bind.split('@');
  let path = components[0];
  let effectId: string | null = null;
  let effectIndex = 0;
  if (path.includes('[')) {
    const arr: string[] = [];
    const regex = /\[([\w\-]+)\]/g;
    let result;
    while ((result = regex.exec(path))) {
      arr.push(result[1]);
    }
    effectId = arr[0];
    if (arr.length > 1) {
      effectIndex = parseInt(arr[1], 10);
    }
    path = path.substr(0, path.indexOf('['));
  }
  let effect: Effect | null = null;

  const propertyName = components[1];
  const node = getNodeById(path);
  if (node && node instanceof Layer) {
    if (effectId) {
      effect = node.effects.filter(e => {
        const { id } = e.constructor as any;
        return id === effectId;
      })[effectIndex];
      if (!effect) {
        console.error(`Couldn't find effect with id ${effectId}`);
      }
    }
  }
  return { node, propertyName, effect };
};

export interface TemplateUIBind {
  node: string;
  options?: TemplateUIBindOptions;
}

export interface TemplateUI {
  label: string;
  type: TemplateUIType;
  bind: string | TemplateUIBind | string[] | TemplateUIBind[];
  library?: string;
  visible?: string;
}

export interface TemplateOptions {
  isAnimated: boolean;
  isPro?: boolean;
}

export type FlatStorableMotionData = {
  audioUrl?: string;
} & Partial<MotionSourceData>;

export function isMotionSourceData(
  data: FlatStorableMotionData
): data is MotionSourceData {
  return 'type' in data;
}

export interface StorableMotionData {
  audioUrl?: string;
  audioThumbUrl?: string;
  audioTitle?: string;
  audioArtist?: string;
  motionSource?: MotionSourceData;
  curve?: CurveData;
}

export interface FlyerData extends StorableMotionData {
  id?: string;
  name?: string;
  soundtrack?: Soundtrack;
  audio?: string; // examples still use it, should deprecate
  layers?: LayerData[];
  resolution: Resolution;
  thumbnailUrl?: string;
  captions: CaptionsToken[] | null;
  captionsLanguageCode: string | null;

  type?: FlyerType;
  private?: boolean;
  features?: string[];
  thumb?: string;
  preview?: string;
  date?: number;
  length?: number; // in seconds
  uid?: string;

  uploadedImages?: string[];
  published?: boolean;
  templateUI?: TemplateUI[];
  templateOptions?: TemplateOptions;
  templateUIIntro?: TemplateUIIntroType;
  categories?: string[];
  usageCount?: number;
}

const getAllFramesUrl = (data: FlyerData): string[] => {
  const result: string[] = [];
  const addUrl = (url: string) => {
    if (result.indexOf(url) === -1) {
      result.push(url);
    }
  };
  if (data.layers) {
    data.layers.forEach(layer => {
      layer.frames.forEach(frame => {
        if (
          !frame.type ||
          frame.type === FrameType.Image ||
          frame.type === FrameType.Video
        ) {
          addUrl((frame as PictureFrameData).url);
        }
      });
    });
  }
  return result;
};

export const getAllImagesUrl = (data: FlyerData): string[] => {
  return getAllFramesUrl(data).filter(url => {
    const extension = url.split('.').pop() as string;
    return extension.toLowerCase() !== 'mp4';
  });
};

export const getAllVideosUrl = (data: FlyerData): string[] => {
  return getAllFramesUrl(data).filter(url => {
    const extension = url.split('.').pop() as string;
    return extension.toLowerCase() === 'mp4';
  });
};

export const createMask = (
  shape: ShapeData,
  hole: ShapeData | undefined = undefined
): PIXI.Container => {
  const maskRect = getShapeRectangle(shape);
  const g = new PIXI.Graphics();

  const drawShape = (data: ShapeData) => {
    const isPolygon = 'corners' in data;
    if (Array.isArray(data) || isPolygon) {
      const points = isPolygon ? getPolygonPoints(data) : data;
      let p = points[0];
      g.moveTo(p.x, p.y);
      for (let i = 1; i < points.length; i++) {
        p = points[i];
        g.lineTo(p.x, p.y);
      }
    } else if (data.isOval) {
      g.drawEllipse(
        data.x + data.width * 0.5,
        data.y + data.height * 0.5,
        data.width * 0.5,
        data.height * 0.5
      );
    } else {
      g.drawRoundedRect(
        data.x,
        data.y,
        data.width,
        data.height,
        data.cornerRadius || 0
      );
    }
  };

  g.beginFill(0xff0000, 1);
  drawShape(shape);
  g.endFill();

  if (hole) {
    g.beginHole();
    drawShape(hole);
    g.endHole();
  }

  const texture = PIXI.RenderTexture.create({
    width: maskRect.width,
    height: maskRect.height
  });
  texture.baseTexture.framebuffer.multisample = PIXI.MSAA_QUALITY.MEDIUM;
  g.position.set(-maskRect.x, -maskRect.y);
  const renderer = getRenderer();
  renderer.render(g, texture);
  // @ts-ignore
  renderer.framebuffer.blit();
  const maskSprite = new PIXI.Sprite(texture);
  // maskSprite.position.set(maskSprite.x, maskSprite.y);

  return maskSprite;
};



// WEBPACK FOOTER //
// ./src/data/FlyerData.ts